import React, { useEffect, useState } from "react";
import { FileDown, FileSpreadsheet, Link2, Loader2, Mail, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { useLang } from "../i18n";

const fmt = (iso) => (iso ? String(iso).slice(0, 16).replace("T", " ") : "—");

export default function PublicRegistry() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fType, setFType] = useState("");
  const [fTenant, setFTenant] = useState("");
  const [busy, setBusy] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/platform/public-forms"), api.get("/forms/public-links/tenants")])
      .then(([f, tn]) => {
        setRows(f.data);
        setTenants(tn.data);
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = rows.filter((r) =>
    (!fType || r.form_type === fType) && (!fTenant || r.tenant_name === fTenant));
  const typeOptions = [...new Set(rows.map((r) => r.form_type).filter(Boolean))].sort();
  const tenantOptions = [...new Set(rows.map((r) => r.tenant_name).filter(Boolean))].sort();

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  const exportFile = async (kind) => {
    setBusy(kind);
    try {
      const p = new URLSearchParams();
      if (fType) p.set("form_type", fType);
      if (fTenant) p.set("tenant", fTenant);
      const res = await api.get(`/platform/public-forms/${kind}?${p}`, { responseType: "blob" });
      downloadBlob(res.data, `registros-enlace-publico.${kind}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const openDetail = async (row) => {
    setDetail({ id: row.id, form_number: row.form_number });
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/platform/public-forms/${row.id}`);
      setDetail(data);
    } catch (e) {
      toast.error(apiError(e));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const pdfRecord = async (rec) => {
    try {
      const res = await api.get(`/platform/public-forms/${rec.id}/pdf`, { responseType: "blob" });
      downloadBlob(res.data, `${rec.form_number}.pdf`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const resendRecord = async (rec) => {
    try {
      await api.post(`/platform/public-forms/${rec.id}/resend`);
      toast.success(t("pr.resent"));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const assign = async (formId, tenantId) => {
    if (!tenantId) return;
    try {
      await api.patch(`/forms/${formId}/assign`, { tenant_id: tenantId });
      toast.success(t("pf.reassigned"));
      setDetail(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="public-registry-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
            <Link2 className="w-6 h-6 text-[#044CBC]" /> {t("pr.title")}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{t("pr.sub")}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportFile("pdf")} disabled={!!busy} data-testid="public-registry-export-pdf" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#044CBC] text-white text-xs font-bold hover:bg-[#033A8C] disabled:opacity-60">
            {busy === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} {t("pr.export_pdf")}
          </button>
          <button onClick={() => exportFile("xlsx")} disabled={!!busy} data-testid="public-registry-export-xlsx" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-800 disabled:opacity-60">
            {busy === "xlsx" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />} {t("pr.export_xlsx")}
          </button>
        </div>
      </div>
      <div className="ec-card p-6">
        <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="public-registry-filters">
          <div>
            <label className="ec-label">{t("pr.module")}</label>
            <select className="ec-input" value={fType} onChange={(e) => setFType(e.target.value)} data-testid="public-registry-filter-type">
              <option value="">{t("pr.all")}</option>
              {typeOptions.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <div>
            <label className="ec-label">{t("pr.org")}</label>
            <select className="ec-input" value={fTenant} onChange={(e) => setFTenant(e.target.value)} data-testid="public-registry-filter-tenant">
              <option value="">{t("pr.all")}</option>
              {tenantOptions.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="bg-[#16335F] text-white text-left">
                  <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pf.col_form")}</th>
                  <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pf.col_title")}</th>
                  <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("pf.col_type")}</th>
                  <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("ah.tenant")}</th>
                  <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("ah.date")}</th>
                  <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-right">{t("pf.reassign")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400" data-testid="public-registry-empty">{t("pr.empty")}</td></tr>
                ) : filtered.map((r, i) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`public-registry-row-${i}`} onClick={() => openDetail(r)}>
                    <td className="px-3 py-2.5 font-mono text-xs font-semibold text-slate-700">{r.form_number}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-700">{r.title || "—"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{r.form_type}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{r.tenant_name || "—"}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{fmt(r.created_at)}</td>
                    <td className="px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                      <select className="ec-input !py-1 !text-xs" value="" onChange={(e) => assign(r.id, e.target.value)} data-testid={`public-registry-assign-${i}`}>
                        <option value="">{t("pf.reassign")}</option>
                        {tenants.filter((x) => x.name !== r.tenant_name).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="ec-card max-w-3xl w-full p-6 max-h-[85vh] overflow-y-auto space-y-4" onClick={(e) => e.stopPropagation()} data-testid="public-record-modal">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading font-bold text-slate-900" data-testid="public-record-title">{detail.form_number}</h3>
                <p className="text-xs text-slate-500">{t("pr.detail")}</p>
              </div>
              <div className="flex items-center gap-2">
                {!detailLoading && detail.form_type && (
                  <>
                    <button onClick={() => resendRecord(detail)} data-testid="public-record-resend" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-bold hover:bg-emerald-800">
                      <Mail className="w-3.5 h-3.5" /> {t("pr.resend")}
                    </button>
                    <button onClick={() => pdfRecord(detail)} data-testid="public-record-pdf" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#044CBC] text-white text-xs font-bold hover:bg-[#033A8C]">
                      <FileDown className="w-3.5 h-3.5" /> {t("pr.export_pdf")}
                    </button>
                  </>
                )}
                <button onClick={() => setDetail(null)} className="text-slate-400 hover:text-slate-700" data-testid="public-record-close"><X className="w-5 h-5" /></button>
              </div>
            </div>
            {detailLoading ? (
              <div className="p-8 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</div>
            ) : (
              <>
                <div className="grid sm:grid-cols-3 gap-3 text-xs">
                  <div><span className="font-bold text-slate-500 block">{t("ah.tenant")}</span>{detail.tenant_name || "—"}</div>
                  <div><span className="font-bold text-slate-500 block">{t("pf.col_type")}</span>{detail.form_type}</div>
                  <div><span className="font-bold text-slate-500 block">{t("ah.date")}</span>{fmt(detail.created_at)}</div>
                </div>
                <div>
                  <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-500 mb-2">{t("pr.data")}</h4>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5" data-testid="public-record-data">
                    {Object.entries(detail.data || {}).map(([k, v]) => (
                      <div key={k} className="text-xs flex gap-2">
                        <span className="font-semibold text-slate-500 shrink-0">{k}:</span>
                        <span className="text-slate-800 break-words">{typeof v === "boolean" ? (v ? t("common.yes") : t("common.no")) : (String(v ?? "—") || "—")}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {(detail.attachments || []).length > 0 && (
                  <div>
                    <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-500 mb-2">{t("k.attachments")}</h4>
                    <div className="flex flex-wrap gap-2" data-testid="public-record-attachments">
                      {detail.attachments.map((a, i) => (
                        <span key={i} className="text-xs bg-slate-100 text-slate-700 rounded-lg px-2.5 py-1">{a.note ? `${a.note}: ` : ""}{a.filename}</span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
